import { DecodedVinResource, ListResource, VehicleResource } from 'services/resources';
import request from '../../request';

/**
 * Returns the vehicles list
 */
export function query(limit: number, offset: number, filters: URLSearchParams, search = '') {
  const params = {
    limit: limit.toString(),
    offset: offset.toString(),
    search,
  };
  const urlParams = new URLSearchParams(params);

  for (const [key, val] of filters.entries()) {
    urlParams.append(key, val);
  }
  return request<ListResource<VehicleResource>>('fleet/vehicles/', {
    method: 'GET',
    params: urlParams,
  });
}

/**
 * Returns the vehicles list to be displayed on the map
 */
export function queryLight(filters: URLSearchParams, search: string) {
  const params = {
    light: 'true',
    is_connected: '1',
    limit: '250',
    search,
  };
  const urlParams = new URLSearchParams(params);

  for (const [key, val] of filters.entries()) {
    urlParams.append(key, val);
  }

  return request('fleet/vehicles/', {
    method: 'GET',
    params: urlParams,
  });
}

/**
 * Creates a new vehicle
 */
export function create(vin, stockNumber, modelId, dealership, exteriorColor, fuelUnit, odometerUnit, fuelType) {
  return request('fleet/vehicles/', {
    method: 'POST',
    data: {
      dealership,
      exteriorColor,
      fuelUnit,
      model: { id: modelId },
      odometerUnit,
      stockNumber,
      vin,
      fuelType,
    },
  });
}

/**
 * Changes the owner of the given vehicles
 */
export function changeOwner(vehicles, owner) {
  return request(`fleet/move/`, {
    method: 'POST',
    data: {
      vehicles,
      owner,
    },
  });
}

/**
 * Decodes a vin number and retrieves the information about the card
 */
export function decodeVin(vin: string) {
  return request<DecodedVinResource>(`common/vin/${vin}/`, {
    method: 'GET',
  });
}

/**
 * Returns the vehicle info
 */
export function querySingleVehicle(id: number) {
  return request<VehicleResource>(`fleet/vehicles/${id}/`, {
    method: 'GET',
    params: {
      withDamages: true,
    },
  });
}

/**
 * Returns the vehicle info
 */
export function getConnectedVehicleData(id) {
  return request(`fleet/vehicles/${id}/connected_data/`, {
    method: 'GET',
  });
}

/**
 * Update the vehicle info and returns the new vehicle info
 */
export function update(id, newVehicleInfo) {
  return request(`fleet/vehicles/${id}/`, {
    method: 'PATCH',
    data: newVehicleInfo,
  });
}

export function getModels(filters: object) {
  return request(`common/vehicle-models/`, {
    method: 'GET',
    params: filters,
  });
}

export function getDepartments() {
  return request(`common/departments/`, {
    method: 'GET',
  });
}

export function getStatuses() {
  return request(`common/vehicle-status/`, {
    method: 'GET',
  });
}

export function partialUpdateModel(id, newModelInfo) {
  return request(`common/vehicle-models/${id}/`, {
    method: 'PATCH',
    data: newModelInfo,
  });
}

export function addModel(newModelInfo) {
  return request(`common/vehicle-models/`, {
    method: 'POST',
    data: newModelInfo,
  });
}

export function addNote(id, note) {
  return request(`common/vehicles/${id}/notes/`, {
    method: 'POST',
    data: {
      vehicle: id,
      note,
    },
  });
}

/**
 * Update a of note
 */
export function patchNote(vehicleId, note) {
  return request(`common/vehicles/${vehicleId}/notes/`, {
    method: 'PATCH',
    data: {
      ...note,
    },
  });
}

/**
 * Set the vehicle's damages
 */
export function setDamages(id, damages) {
  return request(`fleet/vehicles/${id}/`, {
    method: 'PATCH',
    data: { damages },
  });
}

/**
 * Add a vehicle damage
 */
export function createDamage(vehicleId, damage) {
  return request(`fleet/vehicles/${vehicleId}/damages/`, {
    method: 'POST',
    data: damage,
  });
}

/**
 * Remove a vehicle damage
 */
export function deleteDamage(vehicleId, damageId) {
  return request(`fleet/vehicles/${vehicleId}/damages/${damageId}/`, {
    method: 'DELETE',
  });
}

/**
 * Modify a vehicle damage
 */
export function updateDamage(vehicleId, damage) {
  return request(`fleet/vehicles/${vehicleId}/damages/${damage.id}/`, {
    method: 'PATCH',
    data: damage,
  });
}

/**
 * Delete a vehicle damage picture
 */
export function deletePicture(vehicleId, damageId, pictureId) {
  return request(`fleet/vehicles/${vehicleId}/damages/${damageId}/pictures/${pictureId}/`, {
    method: 'DELETE',
  });
}

/**
 * Add a vehicle damage picture
 */
export function createPicture(vehicleId, damageId, pictures) {
  const bodyFormData = new FormData();

  pictures.forEach((file, index) => {
    bodyFormData.append(`FILE_${index}`, file, file.name);
  });

  return request(`fleet/vehicles/${vehicleId}/damages/${damageId}/pictures/`, {
    method: 'POST',
    data: bodyFormData,
    headers: { 'Content-Type': 'multipart/form-data' },
    noKeyTransform: true,
  });
}

/**
 * Returns the vehicle's history list
 */
export function getHistory(id, filter) {
  return request(`fleet/vehicles/${id}/history/?category=${filter}`, {
    method: 'GET',
  });
}

/**
 * Set the vechiles base odometer
 */
export function requestAdjustOffset(id, data) {
  return request(`fleet/vehicles/${id}/adjust_offset/`, {
    method: 'PATCH',
    data,
  });
}
